import { useSelector } from 'react-redux';

import { hasEasypayPayoutAccount } from 'utils/easypayPayout';

/**
 * One answer to "can this promoter create events yet?" (T2B-423).
 *
 * Every entry point into event creation asks this — the header button, the
 * dashboard buttons, the homepage CTA, the /create-event route itself — and
 * the API enforces the same rule independently. Keeping the question in one
 * place is what stops those drifting apart.
 *
 * The three states are deliberately distinct:
 *
 *   resolved=false   we haven't loaded the promoter profile yet. Callers must
 *                    NOT block on this — a spinner or a briefly-dimmed button
 *                    on every page load is worse than one wasted click, and
 *                    the API refuses the request anyway.
 *   blocked=true     promoter, profile loaded, no usable Easypay account.
 *   blocked=false    not a promoter, or connected.
 */
const useEasypayGate = () => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const me = useSelector((state) => state.promoter.me);

  const isPromoter = isAuthenticated && user?.role === 'promoter';

  // `me` only ever holds the signed-in promoter, but the id is checked anyway:
  // between logging in as someone else and AuthBootstrapper's refetch, the
  // previous user's profile is still sitting there, and reading their connected
  // account as ours would open the gate for a promoter who hasn't connected.
  const ownProfile = isPromoter && me?.id === user?.id ? me : null;

  const resolved = !isPromoter || !!ownProfile;
  const connected = !!ownProfile && hasEasypayPayoutAccount(ownProfile);

  return {
    isPromoter,
    resolved,
    connected,
    blocked: isPromoter && resolved && !connected,
    promoter: ownProfile,
  };
};

export default useEasypayGate;
